import { useRef, useState } from 'react';
import type { AdminUser } from '@baex/shared';
import { Button } from '../../components/ui/Button';
import { ErrorNotice } from '../../components/ui/ErrorNotice';
import { TextField } from '../../components/ui/Field';
import { Modal } from '../../components/ui/Modal';
import { focusFirstInvalid } from '../../lib/focusFirstInvalid';
import {
  PASSWORD_MIN_LENGTH,
  validatePassword,
  validatePasswordConfirmation,
} from './passwordRules';

/**
 * Reset password, per the "Admin Users — table and controls" section: one new password, typed twice. Resetting also ends
 * that admin's sessions, so anyone signed in as them is signed out.
 */
export function ResetPasswordModal({
  admin,
  onCancel,
  onSubmit,
}: {
  admin: AdminUser | null;
  onCancel: () => void;
  onSubmit: (email: string, password: string) => Promise<void>;
}) {
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [passwordError, setPasswordError] = useState<string | undefined>(undefined);
  const [confirmationError, setConfirmationError] = useState<string | undefined>(undefined);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const passwordRef = useRef<HTMLInputElement | null>(null);
  const confirmationRef = useRef<HTMLInputElement | null>(null);

  if (!admin) return null;

  function reset() {
    setPassword('');
    setConfirmation('');
    setPasswordError(undefined);
    setConfirmationError(undefined);
    setSubmitError(null);
    setBusy(false);
  }

  async function submit() {
    if (!admin) return;
    const nextPasswordError = validatePassword(password);
    const nextConfirmationError = validatePasswordConfirmation(password, confirmation);
    setPasswordError(nextPasswordError);
    setConfirmationError(nextConfirmationError);
    const atFault = focusFirstInvalid([
      { error: nextPasswordError, ref: passwordRef },
      { error: nextConfirmationError, ref: confirmationRef },
    ]);
    if (atFault) return;

    setBusy(true);
    setSubmitError(null);
    try {
      await onSubmit(admin.email, password);
      reset();
    } catch (error) {
      setSubmitError(error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={`Reset password — ${admin.email}`}
      onClose={() => {
        reset();
        onCancel();
      }}
      width="sm"
      footer={
        <>
          <Button
            onClick={() => {
              reset();
              onCancel();
            }}
          >
            Cancel
          </Button>
          {/* Disabled only while its own request runs (see the "Admin Interface" section). */}
          <Button variant="primary" disabled={busy} onClick={() => void submit()}>
            {busy ? 'Saving…' : 'Reset password'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-[13px] text-ink-muted">
          The current password stops working immediately. Hand the new one over through a secure
          channel.
        </p>
        <TextField
          ref={passwordRef}
          label="New password"
          type="password"
          autoComplete="new-password"
          value={password}
          error={passwordError}
          hint={`At least ${PASSWORD_MIN_LENGTH} characters.`}
          onChange={(event) => {
            setPassword(event.target.value);
            if (passwordError) setPasswordError(validatePassword(event.target.value));
          }}
          onBlur={(event) => setPasswordError(validatePassword(event.target.value))}
        />
        <TextField
          ref={confirmationRef}
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          value={confirmation}
          error={confirmationError}
          onChange={(event) => {
            setConfirmation(event.target.value);
            if (confirmationError)
              setConfirmationError(validatePasswordConfirmation(password, event.target.value));
          }}
          onBlur={(event) =>
            setConfirmationError(validatePasswordConfirmation(password, event.target.value))
          }
        />

        {submitError ? (
          <ErrorNotice error={submitError} fallback="The password could not be reset." />
        ) : null}
      </div>
    </Modal>
  );
}
